import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ClockIcon } from '../../components/icons.js';
import {
  useReport,
  useMyReportParams,
  useSaveMyReportParams,
  useSaveReportAsMine,
} from '../../api/reports.js';
import { useReportSchedules } from '../../api/schedules.js';
import { RenderPanel } from './RenderPanel.js';
import { SchedulePanel } from './SchedulePanel.js';
import { resolveReportViewState } from './report-view-state.js';

/**
 * Consumer view of a report instance — wraps RenderPanel with the
 * report's templateId + default_parameters and nothing else. No
 * Settings / Schedule / Share tabs, no Edit / Rename / Move / Clone /
 * Delete menu, no InlineReportHeader. Just the report's parameters,
 * the Run button, and the rendered output.
 *
 * Used by InsightsPage in embed mode (`?embed=1`). Standalone Sage
 * authors keep the full ReportDetail experience under /reports.
 *
 * Mirrors how ReportsPage > ReportDetail composes RenderPanel on its
 * View tab — same initialParams merge (saved defaults + URL drill-
 * through overrides) so drill-through links from a dashboard widget
 * land the same way they do in the standalone path.
 */
interface Props {
  readonly header?: ReactNode;
  readonly reportId: string;
  /**
   * CSAGE-353: open the Subscriptions modal as soon as this report mounts.
   * Set when the user picked "Subscribe…" from the My Reports kebab, so the
   * kebab action lands them where they intended instead of merely opening the
   * report and making them find the button.
   */
  readonly autoOpenSubscriptions?: boolean;
  readonly onSubscriptionsOpened?: () => void;
}

export function ReportViewer(props: Props): JSX.Element {
  return <ReportViewerInstance key={props.reportId} {...props} />;
}

function ReportViewerInstance({
  reportId,
  header,
  autoOpenSubscriptions,
  onSubscriptionsOpened,
}: Props): JSX.Element {
  const reportQ = useReport(reportId);
  // Per-user saved parameters (Tier-3 `parameters/mine`, CAP3-8940). A shared
  // report renders with the current user's own saved parameter set layered
  // over the instance defaults — matching the legacy Reports module, where
  // each user keeps their own configuration.
  const myParamsQ = useMyReportParams(reportId);
  const saveMyParams = useSaveMyReportParams();
  // CSAGE-353: "Save as my report" — a private copy of this report carrying the
  // parameter values currently on screen. Distinct from "Save as defaults"
  // above, which stores the user's parameter set against the SHARED report;
  // this produces a separate report of their own that they can subscribe to.
  const saveAsMine = useSaveReportAsMine();
  // CSAGE-353: subscriptions bound to THIS instance. Drives the count badge and
  // is the same source the modal now reads, so the badge can never disagree
  // with what opening it shows.
  const schedulesQ = useReportSchedules(reportId);
  const subscriptionCount = schedulesQ.data?.length ?? 0;
  const [subscriptionsOpen, setSubscriptionsOpen] = useState(false);
  /**
   * CSAGE-384: the parameter values on screen when Subscriptions was opened.
   *
   * A subscription used to be created with `params: {}` — it inherited nothing
   * from the report the user was looking at. So a customer who narrowed an
   * inherited report to their own node and then subscribed got a subscription
   * that rendered the report's OWN defaults, which for a report provisioned at
   * an ancestor is the ancestor's whole subtree. Silently, every week.
   *
   * Captured at open time rather than read at submit time so the subscription
   * describes what the user was looking at when they asked for it.
   */
  const [subscriptionParams, setSubscriptionParams] = useState<Record<string, unknown>>({});

  const [readyParams, setReadyParams] = useState<Record<string, unknown> | null>(null);
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoOpenSubscriptions) {
      autoOpened.current = false;
      return;
    }
    if (!readyParams || autoOpened.current) return;
    autoOpened.current = true;
    setSubscriptionParams(readyParams);
    setSubscriptionsOpen(true);
    onSubscriptionsOpened?.();
  }, [autoOpenSubscriptions, readyParams, onSubscriptionsOpened]);

  const myParams = myParamsQ.data?.params;
  const initialParams = useMemo(() => {
    const saved = (reportQ.data?.defaultParameters ?? {}) as Record<string, unknown>;
    const mine = (myParams ?? {}) as Record<string, unknown>;
    const overrides: Record<string, unknown> = {};
    // Allow drill-through: any ?Foo=bar in the URL overrides the
    // instance's saved defaults for this one render. Saved values
    // remain untouched — overrides are session-only. Mirrors the
    // ReportDetail View-tab behaviour so drill-through links land
    // identically inside the embed and in standalone Sage.
    try {
      new URLSearchParams(window.location.search).forEach((value, key) => {
        // Skip our own embed control flag — never a report parameter.
        if (key === 'embed') return;
        overrides[key] = value;
      });
    } catch {
      /* ignore — no window.location is unusual but not fatal */
    }
    // Precedence: instance defaults < my saved params < URL drill-through.
    return { ...saved, ...mine, ...overrides };
  }, [reportQ.data?.defaultParameters, myParams]);

  // CSAGE-364: an error banner requires an actual error. The previous
  // `reportQ.error || !reportQ.data` treated "no data yet" as a failure, which
  // in react-query v5 is a reachable state for a healthy query (see
  // report-view-state.ts) — so a fine report rendered a stuck "Report
  // unavailable." that only a page reload cleared. Waiting for saved params is
  // still deliberate, so RenderPanel mounts once with the merged seed instead of
  // flashing the shared defaults and re-seeding.
  const viewState = resolveReportViewState(
    {
      isError: reportQ.isError,
      error: reportQ.error,
      hasData: reportQ.data !== undefined,
      isPending: reportQ.isPending,
    },
    {
      isError: myParamsQ.isError,
      error: myParamsQ.error,
      hasData: myParamsQ.data !== undefined,
      isPending: myParamsQ.isPending,
    },
  );
  if (viewState.kind === 'loading') {
    return (
      <div className="text-dim" style={{ padding: 'var(--space-4)' }}>
        {header}
        Loading report…
      </div>
    );
  }
  if (viewState.kind === 'error') {
    return (
      <div className="alert alert--danger" style={{ margin: 'var(--space-4)' }}>
        {header}
        <div>Report unavailable.</div>
        {/* The server's own code and message, so the next report of this lands
            with the cause attached instead of costing a round-trip. */}
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
          {viewState.code ? `${viewState.code}: ` : ''}
          {viewState.message}
        </div>
        <button
          type="button"
          className="btn btn--sm"
          style={{ marginTop: 'var(--space-3)' }}
          onClick={() => void reportQ.refetch()}
        >
          Try again
        </button>
      </div>
    );
  }
  const report = reportQ.data!;
  return (
    <div className="report-detail report-detail--full">
      <RenderPanel
        key={reportId}
        templateId={report.templateId}
        templateName={report.name}
        reportId={reportId}
        initialParams={initialParams}
        onParametersReady={setReadyParams}
        onSaveDefaults={async (values) => {
          // "Save as defaults" in the consumer view saves the user's OWN
          // parameter set, never the shared report instance.
          await saveMyParams.mutateAsync({ id: reportId, params: values });
        }}
        onSaveAsMine={async (values, name) => {
          await saveAsMine.mutateAsync({
            id: reportId,
            defaultParameters: values,
            ...(name !== undefined ? { name } : {}),
          });
        }}
        // 'save' when this already IS the caller's copy — the service updates it
        // in place, so no naming dialog. 'copy' otherwise. Derived from
        // visibility; an admin viewing ANOTHER user's personal report will read
        // "Save changes" while the service correctly gives them their own copy —
        // the label is optimistic in that one case, never the behaviour.
        saveCopyMode={report.visibility === 'personal' ? 'save' : 'copy'}
        {...(report.pageOrientation ? { pageOrientation: report.pageOrientation } : {})}
        {...(report.pageSize ? { pageSize: report.pageSize } : {})}
        leftSlot={(liveParams) => (
          <div
            className="report-heading-actions"
            style={{ display: 'flex', alignItems: 'center', gap: 12 }}
          >
            {header}
            <button
              type="button"
              // Secondary weight, not btn--sm: this sits beside Run and Save a
              // copy, and a small ghost button next to two full-size ones read as
              // decoration rather than an action.
              className="btn btn--secondary"
              disabled={!readyParams}
              onClick={() => {
                // CSAGE-384: freeze the on-screen parameters into the modal, so
                // the subscription delivers the report the user is looking at.
                setSubscriptionParams(liveParams);
                setSubscriptionsOpen(true);
              }}
              title={
                subscriptionCount > 0
                  ? `${subscriptionCount} subscription${subscriptionCount === 1 ? '' : 's'} on this report`
                  : 'No subscriptions yet — set up scheduled delivery for this report'
              }
            >
              <ClockIcon style={{ marginRight: 6 }} />
              Subscriptions
              {/* CSAGE-353: always render the count, including 0. A blank was
                  ambiguous — you could not tell "none" from "not loaded yet",
                  which is exactly the doubt this badge exists to remove. The
                  number is also in the title text, so the signal is never
                  shape-or-colour only. */}
              <span className="embed-tab-count" style={{ marginLeft: 6 }}>
                {schedulesQ.isLoading ? '–' : subscriptionCount}
              </span>
            </button>
          </div>
        )}
      />
      {subscriptionsOpen && (
        <SubscriptionsModal
          templateId={report.templateId}
          reportId={reportId}
          reportName={report.name}
          params={subscriptionParams}
          onClose={() => setSubscriptionsOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Lightweight modal wrapping SchedulePanel so the consumer can manage
 * subscriptions without leaving the report view. SchedulePanel already
 * handles all the cron + channel logic; we just provide a frame +
 * close button.
 */
function SubscriptionsModal({
  templateId,
  reportId,
  reportName,
  params,
  onClose,
}: {
  templateId: string;
  reportId: string;
  reportName: string;
  /** CSAGE-384: on-screen parameters a new subscription must inherit. */
  params: Record<string, unknown>;
  onClose(): void;
}): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [editing, setEditing] = useState(false);
  const [paneInsets, setPaneInsets] = useState({ left: 24, right: 24, top: 24, bottom: 24 });
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    const pane =
      dialog?.closest('.preview-reading-pane') ??
      dialog?.closest('.insights-layout')?.querySelector(':scope > section');
    if (!pane) return;
    const update = () => {
      const bounds = pane.getBoundingClientRect();
      setPaneInsets({
        left: Math.max(0, bounds.left) + 24,
        right: Math.max(0, window.innerWidth - bounds.right) + 24,
        top: Math.max(0, bounds.top) + 24,
        bottom: Math.max(0, window.innerHeight - bounds.bottom) + 24,
      });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(pane);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    const previous = document.activeElement as HTMLElement | null;
    dialog?.showModal();
    return () => {
      dialog?.close();
      previous?.focus();
    };
  }, []);
  return (
    <dialog
      ref={dialogRef}
      onCancel={(event) => {
        // A nested subscription editor owns its draft until explicitly closed.
        if (editing) event.preventDefault();
        else {
          event.preventDefault();
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Subscriptions for ${reportName}`}
      style={{
        border: 0,
        margin: 0,
        width: '100vw',
        height: '100vh',
        maxWidth: 'none',
        maxHeight: 'none',
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: `${paneInsets.top}px ${paneInsets.right}px ${paneInsets.bottom}px ${paneInsets.left}px`,
        boxSizing: 'border-box',
      }}
    >
      <div
        className="card subscription-workspace"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 1360,
          width: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <header
          className="subscription-workspace-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 32px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div className="subscription-dialog-heading">
            <h2 style={{ margin: 0, fontSize: 24 }}>Subscriptions</h2>
            <p>{reportName}</p>
          </div>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            disabled={editing}
            aria-label="Close subscriptions"
            title="Close"
          >
            ×
          </button>
        </header>
        <div style={{ overflow: 'auto', minHeight: 0 }}>
          <SchedulePanel
            templateId={templateId}
            reportId={reportId}
            params={params}
            onEditingChange={setEditing}
          />
        </div>
      </div>
    </dialog>
  );
}
